// Generated by ReScript, PLEASE EDIT WITH CARE

import * as CssJs from "bs-css-emotion/src/CssJs.bs.js";
import * as Curry from "rescript/lib/es6/curry.js";
import * as React from "react";
import * as Colors from "common/src/Colors.bs.js";
import * as Global from "common/src/Global.bs.js";
import * as Region from "common/src/Region.bs.js";
import * as TftSet from "common/src/data/TftSet.bs.js";
import * as Caml_obj from "rescript/lib/es6/caml_obj.js";
import * as Axis from "@visx/axis";
import * as Grid from "@visx/grid";
import * as Belt_Array from "rescript/lib/es6/belt_Array.js";
import * as ScreenAtom from "common/src/atoms/ScreenAtom.bs.js";
import * as Curve from "@visx/curve";
import * as Scale from "@visx/scale";
import * as Shape from "@visx/shape";
import * as Belt_Option from "rescript/lib/es6/belt_Option.js";
import * as Caml_option from "rescript/lib/es6/caml_option.js";
import * as LpChartUtils from "common/src/LpChartUtils.bs.js";
import * as Tooltip from "@visx/tooltip";
import Format from "date-fns/format";
import * as Caml_splice_call from "rescript/lib/es6/caml_splice_call.js";
import AddDays from "date-fns/addDays";
import AddHours from "date-fns/addHours";
import GetMonth from "date-fns/getMonth";
import DifferenceInDays from "date-fns/differenceInDays";

function getTimeBounds(daySpan, minTick, maxTick) {
  var xTicks = [];
  minTick.setMinutes(0);
  minTick.setSeconds(0);
  minTick.setMilliseconds(0);
  maxTick.setMinutes(0);
  maxTick.setSeconds(0);
  maxTick.setMilliseconds(0);
  if (daySpan < 16) {
    var minHours = minTick.getHours() | 0;
    var minHours$1 = Math.imul(minHours / 6 | 0, 6);
    minTick.setHours(Global.f(minHours$1));
    var maxHours = maxTick.getHours() | 0;
    var maxHours$1 = Math.imul((maxHours / 6 | 0) + 1 | 0, 6);
    maxTick.setHours(0);
    var maxTick$1 = AddHours(maxTick, maxHours$1);
    var currTick = minTick;
    while(Caml_obj.caml_lessthan(currTick, maxTick$1)) {
      xTicks.push(currTick);
      currTick = AddHours(currTick, 6);
    };
    xTicks.push(maxTick$1);
  } else if (daySpan < 32) {
    var minHours$2 = minTick.getHours() | 0;
    var minHours$3 = Math.imul(minHours$2 / 12 | 0, 12);
    minTick.setHours(Global.f(minHours$3));
    var maxHours$2 = maxTick.getHours() | 0;
    var maxHours$3 = Math.imul((maxHours$2 / 12 | 0) + 1 | 0, 12);
    maxTick.setHours(0);
    var maxTick$2 = AddHours(maxTick, maxHours$3);
    var currTick$1 = minTick;
    while(Caml_obj.caml_lessthan(currTick$1, maxTick$2)) {
      xTicks.push(currTick$1);
      currTick$1 = AddHours(currTick$1, 12);
    };
    xTicks.push(maxTick$2);
  } else {
    minTick.setHours(0);
    var maxTick$3 = AddDays(maxTick, 1);
    maxTick$3.setHours(0);
    var currTick$2 = minTick;
    while(Caml_obj.caml_lessthan(currTick$2, maxTick$3)) {
      xTicks.push(currTick$2);
      currTick$2 = AddHours(currTick$2, 24);
    };
    xTicks.push(maxTick$3);
  }
  return xTicks;
}

function PlTimeChart(Props) {
  var region = Props.region;
  var matches = Props.matches;
  var padLeft = Props.padLeft;
  var chartWidth = Props.chartWidth;
  var chartHeight = Props.chartHeight;
  var masterRanks = Props.masterRanks;
  var screenSize = ScreenAtom.use(undefined);
  var dateTimes = Belt_Array.map(matches, (function (m) {
          return m.dateTime;
        }));
  var minDate = new Date(Caml_splice_call.spliceApply(Math.min, [dateTimes]));
  var maxDate = new Date(Caml_splice_call.spliceApply(Math.max, [dateTimes]));
  var daySpan = DifferenceInDays(maxDate, minDate) + 1 | 0;
  var tooltip = Tooltip.useTooltip();
  var set = TftSet.use(undefined);
  var masterLp = set === 10 || set >= 100 ? 2800 : 2400;
  var xData = Belt_Array.map(getTimeBounds(daySpan, new Date(Caml_splice_call.spliceApply(Math.min, [dateTimes])), new Date(Caml_splice_call.spliceApply(Math.max, [dateTimes]))), (function (prim) {
          return prim.getTime();
        }));
  var yData = Belt_Array.map(xData, (function (timestamp) {
          return Belt_Option.flatMap(Caml_option.undefined_to_opt(matches.find(function (match) {
                              if (match.dateTime < timestamp) {
                                return Belt_Option.isSome(match.rankAfter);
                              } else {
                                return false;
                              }
                            })), (function (match) {
                        return match.rankAfter;
                      }));
        }));
  var revMatches = Belt_Array.reverse(matches);
  var i = 0;
  while(Belt_Option.isNone(Belt_Array.getExn(yData, i))) {
    Belt_Array.set(yData, i, Belt_Option.getExn(Caml_option.undefined_to_opt(revMatches.find(function (m) {
                      return Belt_Option.isSome(m.rankAfter);
                    }))).rankAfter);
    i = i + 1 | 0;
  };
  var yData$1 = Belt_Array.map(Belt_Array.map(yData, Belt_Option.getExn), (function (rank) {
          return Global.f(LpChartUtils.divisionToBase(rank[0], set) + rank[1] | 0);
        }));
  var xScale = Scale.scaleLinear({
        range: [
          0,
          Global.f(chartWidth)
        ],
        domain: [
          Belt_Array.getExn(xData, 0),
          Belt_Array.getExn(xData, xData.length - 1 | 0)
        ]
      });
  var xScaleRev = Scale.scaleLinear({
        range: [
          Belt_Array.getExn(xData, 0),
          Belt_Array.getExn(xData, xData.length - 1 | 0)
        ],
        domain: [
          0,
          Global.f(chartWidth)
        ]
      });
  var minYTick = Caml_splice_call.spliceApply(Math.min, [yData$1]);
  var maxYTick = Caml_splice_call.spliceApply(Math.max, [yData$1]);
  var match = LpChartUtils.getYTicks(minYTick | 0, maxYTick | 0);
  var maxRank = match[2];
  var minRank = match[1];
  var yTicks = match[0];
  var yScale = Scale.scaleLinear({
        range: [
          Global.f(chartHeight),
          0
        ],
        domain: [
          Global.f(minRank),
          Global.f(maxRank)
        ]
      });
  var xTicks = Belt_Array.keepWithIndex(xData, (function (param, idx) {
          return idx % 4 === 0;
        }));
  var challPath = Belt_Option.map(masterRanks, (function (mr) {
          var chall = Belt_Array.reverse(mr.challenger);
          return Belt_Array.keep(Belt_Array.keepMap(xTicks, (function (baseTs) {
                            var tickTs = baseTs / 1000;
                            return Belt_Option.map(Caml_option.undefined_to_opt(chall.find(function (param) {
                                                return param[0] > tickTs;
                                              })), (function (param) {
                                          return [
                                                  baseTs,
                                                  param[1] + masterLp | 0
                                                ];
                                        }));
                          })), (function (param) {
                        var lp = param[1];
                        if (lp >= minRank) {
                          return lp <= maxRank;
                        } else {
                          return false;
                        }
                      }));
        }));
  var gmPath = Belt_Option.map(masterRanks, (function (mr) {
          var gm = Belt_Array.reverse(mr.grandmaster);
          return Belt_Array.keep(Belt_Array.keepMap(xTicks, (function (baseTs) {
                            var tickTs = baseTs / 1000;
                            return Belt_Option.map(Caml_option.undefined_to_opt(gm.find(function (param) {
                                                return param[0] > tickTs;
                                              })), (function (param) {
                                          return [
                                                  baseTs,
                                                  param[1] + masterLp | 0
                                                ];
                                        }));
                          })), (function (param) {
                        var lp = param[1];
                        if (lp >= minRank) {
                          return lp <= maxRank;
                        } else {
                          return false;
                        }
                      }));
        }));
  var prevMonthLabel = {
    contents: -1
  };
  var svgWidth = (padLeft + chartWidth | 0) + 10 | 0;
  var svgHeight = (20 + chartHeight | 0) + 41 | 0;
  var patchLines = Belt_Array.keepMap(LpChartUtils.patchDays, (function (param) {
          var patch = param[1];
          var patchTime = AddHours(new Date(param[0]), 4 - Region.rankedUpdateUtcHour2(region) | 0).getTime();
          if (patchTime > minDate.getTime() && patchTime < maxDate.getTime()) {
            return Belt_Option.map(Caml_option.undefined_to_opt(xData.find(function (ts) {
                                return ts > patchTime;
                              })), (function (v) {
                          return [
                                  v,
                                  patch
                                ];
                        }));
          }
          
        }));
  var tmp;
  if (tooltip.tooltipOpen) {
    var idx = Curry._1(xScaleRev, tooltip.tooltipLeft - Global.f(padLeft));
    var xIdxs = Belt_Array.map(xData, (function (x) {
            return Math.abs(x - idx);
          }));
    var minXIdx = Caml_splice_call.spliceApply(Math.min, [xIdxs]);
    var idx$1 = xIdxs.indexOf(minXIdx);
    var x = Belt_Array.getExn(xData, idx$1);
    var left = Curry._1(xScale, x);
    tmp = React.createElement("line", {
          stroke: "rgba(255, 255, 255, 0.55)",
          strokeDasharray: "4,4",
          strokeWidth: "1.75",
          x1: left.toString(),
          x2: left.toString(),
          y1: "0",
          y2: String(chartHeight)
        });
  } else {
    tmp = null;
  }
  var tmp$1;
  if (tooltip.tooltipOpen) {
    var idx$2 = Curry._1(xScaleRev, tooltip.tooltipLeft - Global.f(padLeft));
    var xIdxs$1 = Belt_Array.map(xData, (function (x) {
            return Math.abs(x - idx$2);
          }));
    var minXIdx$1 = Caml_splice_call.spliceApply(Math.min, [xIdxs$1]);
    var idx$3 = xIdxs$1.indexOf(minXIdx$1);
    var x$1 = Belt_Array.getExn(xData, idx$3);
    var y = Belt_Array.getExn(yData$1, idx$3);
    var match$1 = LpChartUtils.ratingToDivisionFull(y | 0, set);
    var lp = match$1[1];
    var div = match$1[0];
    var content = div === "Master" ? String(lp) + " lp" : div + " " + String(lp) + "lp";
    tmp$1 = React.createElement(Tooltip.TooltipWithBounds, {
          left: Curry._1(xScale, x$1) + Global.f(padLeft),
          top: tooltip.tooltipTop + 14,
          className: CssJs.style([CssJs.important(CssJs.background(CssJs.hex(Colors.background)))]),
          children: React.createElement("div", {
                className: "flex text-white1 text-xs sm:text-sm"
              }, React.createElement("div", {
                    className: "pr-2"
                  }, React.createElement("div", {
                        className: "flex items-center"
                      }, React.createElement("div", {
                            className: CssJs.style([
                                  CssJs.width(CssJs.px(8)),
                                  CssJs.height(CssJs.px(8)),
                                  CssJs.unsafe("background", "#" + Colors.accent),
                                  CssJs.marginRight(CssJs.px(4))
                                ])
                          }), Format(new Date(x$1), "MMM do"))), React.createElement("div", undefined, React.createElement("div", undefined, content))),
          key: Math.random().toString()
        });
  } else {
    tmp$1 = null;
  }
  return React.createElement(React.Fragment, undefined, React.createElement("svg", {
                  className: CssJs.style([CssJs.width(CssJs.px(svgWidth))]),
                  viewBox: "0 0 " + String(svgWidth) + " " + String(svgHeight)
                }, React.createElement("g", {
                      onMouseLeave: (function (param) {
                          tooltip.hideTooltip();
                          
                        }),
                      onMouseMove: (function (e) {
                          tooltip.showTooltip({
                                tooltipLeft: e.nativeEvent.offsetX,
                                tooltipTop: e.nativeEvent.offsetY,
                                tooltipData: undefined
                              });
                          
                        }),
                      transform: "translate(" + String(padLeft) + ", " + String(20) + ")"
                    }, React.createElement("rect", {
                          height: String((20 + chartHeight | 0) + 41 | 0),
                          width: String((padLeft + chartWidth | 0) + 10 | 0),
                          fill: "transparent",
                          x: String(-padLeft | 0),
                          y: String(-20)
                        }), React.createElement(Grid.Grid, {
                          top: 0,
                          left: 0,
                          xScale: xScale,
                          yScale: yScale,
                          stroke: "rgba(255, 255, 255, 0.24)",
                          width: chartWidth,
                          height: chartHeight,
                          numTicksRows: 5,
                          numTicksColumns: 8,
                          columnTickValues: xTicks,
                          rowTickValues: yTicks
                        }), React.createElement(Grid.GridRows, {
                          top: 0,
                          left: 0,
                          scale: yScale,
                          stroke: "rgba(255, 255, 255, 0.5)",
                          width: chartWidth,
                          height: chartHeight,
                          tickValues: Belt_Array.keep([
                                400,
                                800,
                                1200,
                                1600,
                                2000,
                                2400,
                                2800
                              ], (function (r) {
                                  if (r >= Global.f(minRank)) {
                                    return r <= Global.f(maxRank);
                                  } else {
                                    return false;
                                  }
                                }))
                        }), React.createElement(Axis.AxisLeft, {
                          top: 0,
                          left: 0,
                          scale: yScale,
                          numTicks: 5,
                          stroke: "white",
                          tickValues: yTicks,
                          tickStroke: "white",
                          tickFormat: (function (v) {
                              var match = LpChartUtils.ratingToDivision(v | 0, set);
                              var lp = match[1];
                              var div = match[0];
                              if (div === "M" && lp !== 0) {
                                return String(lp);
                              } else {
                                return div;
                              }
                            }),
                          tickLabelProps: (function (param) {
                              return LpChartUtils.tickLabelPropsLeft(screenSize, set, param);
                            })
                        }), React.createElement(Axis.AxisBottom, {
                          top: chartHeight,
                          left: 0,
                          scale: xScale,
                          numTicks: 8,
                          stroke: "white",
                          tickValues: xTicks,
                          tickStroke: "white",
                          tickFormat: (function (v) {
                              var date = new Date(v);
                              var month = GetMonth(date);
                              if (prevMonthLabel.contents !== month) {
                                prevMonthLabel.contents = month;
                                return Format(date, "MMM d");
                              } else {
                                return Format(date, "d");
                              }
                            }),
                          tickLabelProps: (function (param) {
                              return LpChartUtils.tickLabelPropsBottom(screenSize, param);
                            })
                        }), Belt_Array.map(patchLines, (function (param) {
                            var idx = param[0];
                            return React.createElement(React.Fragment, {
                                        children: null,
                                        key: String(idx)
                                      }, React.createElement(Shape.Line, {
                                            opacity: "0.7",
                                            strokeDasharray: "6,5",
                                            stroke: "var(--primary-11)",
                                            from: {
                                              x: Curry._1(xScale, idx),
                                              y: -6
                                            },
                                            to: {
                                              x: Curry._1(xScale, idx),
                                              y: Global.f(chartHeight)
                                            },
                                            strokeWidth: 1
                                          }), React.createElement("text", {
                                            style: Global.s({
                                                  fontWeight: "400"
                                                }),
                                            fontFamily: "Montserrat",
                                            fontSize: "12px",
                                            stroke: "var(--primary-11)",
                                            textAnchor: "middle",
                                            x: Math.round(Curry._1(xScale, idx)).toFixed(),
                                            y: "-10"
                                          }, param[1]));
                          })), tmp, gmPath !== undefined ? React.createElement(Shape.LinePath, {
                            opacity: "0.8",
                            strokeDasharray: "3 7",
                            strokeWidth: 1.5,
                            data: gmPath,
                            x: (function (param, param$1) {
                                return Curry._1(xScale, param[0]);
                              }),
                            y: (function (param) {
                                return Curry._1(yScale, Global.f(param[1]));
                              }),
                            stroke: "var(--red-9)",
                            curve: Curve.curveCatmullRom
                          }) : null, challPath !== undefined ? React.createElement(Shape.LinePath, {
                            opacity: "0.8",
                            strokeDasharray: "3 7",
                            strokeWidth: 1.5,
                            data: challPath,
                            x: (function (param, param$1) {
                                return Curry._1(xScale, param[0]);
                              }),
                            y: (function (param) {
                                return Curry._1(yScale, Global.f(param[1]));
                              }),
                            stroke: "var(--amber-9)",
                            curve: Curve.curveCatmullRom
                          }) : null, React.createElement(Shape.LinePath, {
                          strokeWidth: 2.25,
                          data: Belt_Array.zip(xData, yData$1),
                          x: (function (param, param$1) {
                              return Curry._1(xScale, param[0]);
                            }),
                          y: (function (param) {
                              return Curry._1(yScale, param[1]);
                            }),
                          stroke: "#" + Colors.accent,
                          curve: Curve.curveCatmullRom
                        }))), tmp$1);
}

var padRight = 10;

var padTop = 20;

var padBottom = 41;

var make = PlTimeChart;

export {
  padRight ,
  padTop ,
  padBottom ,
  getTimeBounds ,
  make ,
  
}
/* CssJs Not a pure module */
